import styled from "@emotion/styled";
import { getProductColorMap } from "@atlas-int/tokens/classifications";
import {
  ChevronDown,
  ChevronUp,
  DropdownToggleButton,
  DropdownItem,
} from "@atlas-int/ui";
import { memo } from "react";
import type { TreeNode } from "@atlas-int/ui";
import { ProductItem, ProductItemLevel } from "./ProductDropdownUtils";
import {
  text,
  surface,
  controlFontSize,
  spacing,
  zIndex,
  nav,
  border,
} from "@atlas-int/tokens";

const AllProductsStickyItem = styled(DropdownItem)`
  position: sticky;
  top: 0;
  left: 0;
  z-index: ${zIndex.dropdown};
  border-bottom: 1px solid ${border.divider};

  background-color: ${({ $highlighted }) =>
    $highlighted ? nav.hover : nav.surface};
  color: ${text.onDark};

  &:hover {
    background-color: ${nav.hover};
    color: ${text.onDark};
  }
`;

const IconContainer = styled.div`
  flex-basis: ${spacing[2]};
  width: ${spacing[2]};
  align-self: stretch;
`;

const LabelContainer = styled.div`
  flex-basis: 100%;
  padding-left: ${spacing[2]};
  overflow: hidden;
  text-overflow: ellipsis;
  padding-top: ${spacing[4]};
  padding-bottom: ${spacing[4]};
  display: flex;
  flex-direction: row;
  align-content: flex-start;
  margin: auto 0px;
`;

const ProductCodeLabel = styled.span`
  display: contents;
  color: ${text.muted};
  font-size: ${controlFontSize.sm};
`;

function ChevronIcon({ open }: { open: boolean }) {
  return open ? <ChevronUp /> : <ChevronDown />;
}

interface ProductDropdownItemMUIProps {
  node: TreeNode;
  item: ProductItem;
  style: React.CSSProperties;
  isExpanded: boolean;
  isSelected: boolean;
  isHighlighted: boolean;
  hasChildren: boolean;
  level: number;
  onToggle: () => void;
  onClick: () => void;
  productClass: string;
  /** ID of the "All Products" item, used to identify the sticky header */
  allProductsId?: string;
}

const ProductDropdownItemMUI = ({
  item,
  isExpanded,
  isSelected,
  isHighlighted,
  hasChildren,
  onToggle,
  onClick,
  productClass,
  allProductsId,
}: ProductDropdownItemMUIProps) => {
  const {
    id,
    level: productLevel,
    product_section,
    name_short_en,
    code,
  } = item;

  const useColorMap = getProductColorMap(productClass);

  let color: string | undefined = undefined;
  if (useColorMap) {
    if (
      id !== allProductsId &&
      product_section &&
      useColorMap.has(product_section.productId)
    ) {
      color = useColorMap.get(product_section.productId);
    }
  }

  const productCodeLabel = code ? (
    <ProductCodeLabel>
      ({code} {productClass})
    </ProductCodeLabel>
  ) : null;

  const completeItemLabel = productCodeLabel ? (
    <>
      {name_short_en} {productCodeLabel}
    </>
  ) : (
    name_short_en
  );

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };

  // All Products - sticky at top with dark nav-toned background
  if (productLevel === ProductItemLevel.AllProducts) {
    return (
      <AllProductsStickyItem
        $alignItems="start"
        $height="auto"
        $highlighted={isHighlighted}
        $selected={isSelected}
        onClick={onClick}
      >
        <LabelContainer>{completeItemLabel}</LabelContainer>
      </AllProductsStickyItem>
    );
  }

  // Section - with thick colored border
  if (productLevel === ProductItemLevel.ProductSection) {
    return (
      <DropdownItem
        $alignItems="start"
        $height="auto"
        $highlighted={isHighlighted}
        $selected={isSelected}
        onClick={onClick}
        style={{ paddingLeft: 0 }}
      >
        <IconContainer
          style={{
            borderLeft: `${spacing[6]} solid ${color}`,
            width: spacing[6],
            marginTop: 2,
            marginBottom: 2,
          }}
        />
        <LabelContainer>{completeItemLabel}</LabelContainer>
        {hasChildren && (
          <DropdownToggleButton type="button" onClick={handleToggleClick} style={{ alignSelf: "center" }}>
            <ChevronIcon open={isExpanded} />
          </DropdownToggleButton>
        )}
      </DropdownItem>
    );
  }

  // Two-digit
  if (productLevel === ProductItemLevel.Product2digit) {
    return (
      <DropdownItem
        $alignItems="start"
        $height="auto"
        $highlighted={isHighlighted}
        $selected={isSelected}
        onClick={onClick}
        style={{ paddingLeft: 0 }}
      >
        <IconContainer
          style={{
            borderLeft: `3px solid ${color}`,
            marginLeft: spacing[6],
            marginTop: 1,
            marginBottom: 1,
          }}
        />
        <LabelContainer>{completeItemLabel}</LabelContainer>
        {hasChildren && (
          <DropdownToggleButton type="button" onClick={handleToggleClick} style={{ alignSelf: "center" }}>
            <ChevronIcon open={isExpanded} />
          </DropdownToggleButton>
        )}
      </DropdownItem>
    );
  }

  // Four-digit
  if (productLevel === ProductItemLevel.Product4digit) {
    return (
      <DropdownItem
        $alignItems="start"
        $height="auto"
        $highlighted={isHighlighted}
        $selected={isSelected}
        onClick={onClick}
        style={{ paddingLeft: 0 }}
      >
        <IconContainer
          style={{
            borderLeft: `3px solid ${color}`,
            marginLeft: spacing[12],
            marginTop: 1,
            marginBottom: 1,
          }}
        />
        <LabelContainer>{completeItemLabel}</LabelContainer>
        {hasChildren && (
          <DropdownToggleButton type="button" onClick={handleToggleClick} style={{ alignSelf: "center" }}>
            <ChevronIcon open={isExpanded} />
          </DropdownToggleButton>
        )}
      </DropdownItem>
    );
  }

  // Six-digit
  if (productLevel === ProductItemLevel.Product6digit) {
    return (
      <DropdownItem
        $alignItems="start"
        $height="auto"
        $highlighted={isHighlighted}
        $selected={isSelected}
        onClick={onClick}
        style={{ paddingLeft: 0 }}
      >
        <IconContainer
          style={{
            borderLeft: `3px solid ${color}`,
            marginLeft: spacing[16],
            marginTop: 1,
            marginBottom: 1,
          }}
        />
        <LabelContainer>{completeItemLabel}</LabelContainer>
      </DropdownItem>
    );
  }

  return null;
};

export default memo(ProductDropdownItemMUI);
